import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { from, Observable, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';

const BASE_URL = environment.base_url_second;

@Injectable({
    providedIn: 'root'
})
export class VolumentalService {

    private widgetBaseUrl = environment.volumental.widgetUrl;
    private authUrl = `${BASE_URL}/volumental/login`
    private scanDetailsUrl = `${BASE_URL}/volumentalScan`
    private accessToken: string | null = null;
    private tokenExpiration = 0;

    constructor(private http: HttpClient) { }

    private getAccessToken(): Observable<string> {
        const now = Date.now();

        if (this.accessToken && now < this.tokenExpiration) {
            return new Observable(obs => {
                obs.next(this.accessToken!);
                obs.complete();
            });
        }

        return this.http.post<any>(this.authUrl, null).pipe(
            switchMap(res => {
                this.accessToken = res.access_token;
                this.tokenExpiration = Date.now() + (res.expires_in - 60) * 1000;
                return from([this.accessToken]);
            })
        );
    }

    private authHeaders(token: string): HttpHeaders {
        return new HttpHeaders({
            Authorization: `Bearer ${token}`
        });
    }

    async hashEmail(email: string): Promise<string> {
        const normalized = email.toLowerCase();
        const encoder = new TextEncoder();
        const data = encoder.encode(normalized);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }

    getScans(options?: {
        email?: string;
        subject?: string;
        page?: number;
        perPage?: number;
    }): Observable<any> {
        return this.getAccessToken().pipe(
            switchMap(token => {
                let params = new HttpParams();

                if (options?.subject) params = params.set('subject', options.subject);
                if (options?.page) params = params.set('page', options.page);
                if (options?.perPage) params = params.set('per_page', options.perPage);

                const request$ = options?.email
                    ? from(this.hashEmail(options.email)).pipe(
                        switchMap(hash => {
                            const emailParams = params.set('email_hash', hash);
                            return this.http.get(
                                `${this.widgetBaseUrl}/api/v1/scans/web`,
                                {
                                    headers: this.authHeaders(token),
                                    params: emailParams
                                }
                            );
                        })
                    )
                    : this.http.get(
                        `${this.widgetBaseUrl}/api/v1/scans/web`,
                        {
                            headers: this.authHeaders(token),
                            params
                        }
                    );

                return request$;
            })
        );
    }

    getScanDetails(scanId: string): Observable<any> {
        return this.getAccessToken().pipe(
            switchMap(token =>
                this.http.get(
                    `${this.scanDetailsUrl}/${scanId}`,
                    {
                        headers: this.authHeaders(token)
                    }
                )
            )
        );
    }

    getRecommendations(scanId: string): Observable<any> {
        return this.getAccessToken().pipe(
            switchMap(token =>
                this.http.post(
                    `${this.widgetBaseUrl}/api/v1/recommendations`,
                    { scan_id: scanId },
                    {
                        headers: new HttpHeaders({
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        })
                    }
                )
            )
        );
    }
}
